import { useEffect, useState } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';
import {
  BarChart3,
  BookOpen,
  BookOpenCheck,
  FilePenLine,
  LayoutDashboard,
  Library,
  LogOut,
  Menu,
  MessageSquare,
  Mic,
  Settings,
  X,
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';

const navBase =
  'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors duration-150 ease-out motion-reduce:transition-none';
const navIdle = 'text-muted-foreground hover:bg-muted/70 hover:text-foreground';
const navActive = 'bg-primary text-primary-foreground shadow-sm';

function navClassName(isActive) {
  return `${navBase} ${isActive ? navActive : navIdle}`;
}

function UserAvatar({ user }) {
  const label = (user?.name || user?.email || 'User').trim();
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('') || 'U';

  if (user?.picture) {
    return (
      <img
        src={user.picture}
        alt={label}
        className="h-9 w-9 rounded-full border border-border/80 object-cover shadow-sm"
        referrerPolicy="no-referrer"
      />
    );
  }

  return (
    <div className="flex h-9 w-9 items-center justify-center rounded-full border border-border/80 bg-muted text-xs font-semibold text-foreground shadow-sm">
      {initials}
    </div>
  );
}

function useSectionFlags() {
  const { pathname } = useLocation();
  return {
    study: pathname.startsWith('/review') || pathname.startsWith('/qna-review') || pathname.startsWith('/exams'),
    capture: pathname.startsWith('/lecture'),
  };
}

function ShellNavLinks({ onNavigate }) {
  const flags = useSectionFlags();

  return (
    <nav className="flex flex-col gap-0.5 px-2" aria-label="Primary">
      <NavLink to="/dashboard" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <LayoutDashboard className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Dashboard
      </NavLink>
      <NavLink to="/lecture" onClick={onNavigate} className={() => navClassName(flags.capture)}>
        <Mic className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Capture
      </NavLink>
      <NavLink to="/qa" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <MessageSquare className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Ask
      </NavLink>
      <NavLink to="/review" onClick={onNavigate} className={() => navClassName(flags.study)}>
        <BookOpen className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Study
      </NavLink>
      <NavLink to="/mastery" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <BookOpenCheck className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Mastery
      </NavLink>
      <NavLink to="/library" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <Library className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Library
      </NavLink>
      <NavLink to="/notes" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <FilePenLine className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Notes
      </NavLink>
      <NavLink to="/progress" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <BarChart3 className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Progress
      </NavLink>
      <NavLink to="/settings" end onClick={onNavigate} className={({ isActive }) => navClassName(isActive)}>
        <Settings className="h-4 w-4 shrink-0 opacity-90" aria-hidden />
        Settings
      </NavLink>
    </nav>
  );
}

export default function AppShell() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!mobileOpen) return undefined;
    const onKey = (event) => {
      if (event.key === 'Escape') setMobileOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mobileOpen]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="flex min-h-[100dvh] w-full bg-background">
      <aside className="relative z-30 hidden w-56 shrink-0 flex-col border-r border-border bg-card/30 md:flex" aria-label="App navigation">
        <div className="flex h-14 items-center border-b border-border/80 px-4">
          <Link to="/dashboard" className="flex items-center gap-3 text-foreground">
            <UserAvatar user={user} />
            <div className="min-w-0">
              <p className="font-serif text-lg tracking-tight">LectureAI</p>
              {user?.name ? <p className="truncate text-xs text-muted-foreground">{user.name}</p> : null}
            </div>
          </Link>
        </div>
        <div className="flex flex-1 flex-col py-4">
          <ShellNavLinks />
          <div className="mt-auto space-y-1 border-t border-border/80 px-2 pt-4">
            <Button
              variant="ghost"
              className="w-full justify-start gap-3 text-muted-foreground hover:text-foreground"
              onClick={handleLogout}
              data-testid="logout-btn"
            >
              <LogOut className="h-4 w-4 shrink-0" aria-hidden />
              Sign out
            </Button>
          </div>
        </div>
      </aside>

      {mobileOpen ? (
        <div className="fixed inset-0 z-40 md:hidden" role="dialog" aria-modal="true" aria-label="Navigation menu">
          <button
            type="button"
            className="absolute inset-0 bg-black/50"
            aria-label="Close menu"
            onClick={() => setMobileOpen(false)}
          />
          <div className="absolute left-0 top-0 flex h-full w-[min(18rem,100%)] flex-col border-r border-border bg-background shadow-lg">
            <div className="flex h-14 items-center justify-between border-b border-border px-3">
              <Link to="/dashboard" className="flex items-center gap-3 text-foreground" onClick={() => setMobileOpen(false)}>
                <UserAvatar user={user} />
                <div className="min-w-0">
                  <p className="font-serif text-lg">LectureAI</p>
                  {user?.name ? <p className="truncate text-xs text-muted-foreground">{user.name}</p> : null}
                </div>
              </Link>
              <Button type="button" variant="ghost" size="icon" aria-label="Close navigation" onClick={() => setMobileOpen(false)}>
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex flex-1 flex-col overflow-y-auto py-3">
              <ShellNavLinks onNavigate={() => setMobileOpen(false)} />
              <div className="mt-auto border-t border-border px-2 pt-4">
                <Button
                  variant="ghost"
                  className="w-full justify-start gap-3"
                  onClick={() => {
                    setMobileOpen(false);
                    handleLogout();
                  }}
                >
                  <LogOut className="h-4 w-4 shrink-0" aria-hidden />
                  Sign out
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : null}

      <div className="flex min-h-0 flex-1 flex-col">
        <header className="sticky top-0 z-20 flex h-14 shrink-0 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur-sm md:hidden">
          <Button type="button" variant="ghost" size="icon" aria-label="Open navigation menu" onClick={() => setMobileOpen(true)}>
            <Menu className="h-5 w-5" />
          </Button>
          <Link to="/dashboard" className="font-serif text-lg text-foreground">
            LectureAI
          </Link>
        </header>

        <main className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
